import React, { useState, useEffect, useRef } from "react";
import {
  Autocomplete as MuiAutocomplete,
  createFilterOptions,
  Box,
  Typography,
  Chip,
  Select,
  MenuItem,
  ListSubheader,
  FormControl,
  TextField,
  Tooltip,
} from "@mui/material";
import { NumericFormat } from "react-number-format";
import { colors } from "../../../styles/colors";
import { borderRadius, floatingLabelMaskSx } from "../../../styles/theme";
import InfoTooltip from "../overlays/InfoTooltip";

const CUSTOM_INPUT_MARKER = "__custom_input__";
/** Wert des Aktionseintrags am Listenende (`listenAktion`) – nie ein echter Wert */
const LISTEN_AKTION_MARKER = "__listen_aktion__";

type DropdownOption =
  | string
  | {
      value: string;
      label: string;
      /** Hover-Erklärung für diese Option */
      tooltip?: React.ReactNode;
      /** Zeigt einen Trenner oberhalb dieser Option (z.B. vor einer Gruppe von Ausnahmezuständen) */
      dividerBefore?: boolean;
      /**
       * Ordnet die Option einer Gruppe zu. Wechselt der Wert gegenüber der vorherigen Option,
       * steht die Bezeichnung als Überschrift im Menü. Optionen ohne Gruppe (z.B. ein
       * "– kein Wert –"-Eintrag am Anfang) bleiben überschriftslos.
       */
      groupLabel?: string;
    };
type Variant = "side-label" | "floating";

interface DropdownProps {
  label?: string;
  value: string;
  options: DropdownOption[];
  onChange: (value: string) => void;
  getColor?: (value: string) => string;
  /** Überschreibt die Textfarbe einer Option/des aktuellen Werts (z.B. für Ausnahmezustände) */
  getTextColor?: (value: string) => string | undefined;
  /** Kennzeichnet einen Wert als Platzhalter (z.B. "nicht gestartet") - wird kursiv/gedimmt dargestellt */
  isPlaceholder?: (value: string) => boolean;
  disabled?: boolean;
  width?: number | string;
  /** Breite des Side-Labels (nur bei variant='side-label') */
  labelWidth?: number;
  inputWidth?: number | string;
  sx?: object;
  /** Fehlermeldung (z.B. für Formular-Validierung) */
  error?: boolean;
  /** Hilfetext unter dem Feld */
  helperText?: string;
  /**
   * Markiert das Feld als Pflichtfeld – bei variant='floating' übernimmt MUI dafür sein
   * eingebautes Sternchen hinter dem Label (`FormLabel`/`InputLabel`), keine eigene Lösung.
   */
  required?: boolean;
  /** Erklärt das Feld bei Hover übers Label: was es ist und welche Auswirkung es hat */
  tooltip?: React.ReactNode;
  /** MUI-Größe (Standard: 'small' bei side-label, 'medium' bei floating) */
  size?: "small" | "medium";
  /** Layout-Variante: 'side-label' (Standard) oder 'floating' (MUI Floating-Label) */
  variant?: Variant;
  /**
   * Hintergrund hinter dem hochgewanderten Label bei variant='floating' – maskiert den
   * Umrandungsstrich, der sonst hinter dem Label durchscheint (MUI berechnet die Notch-Breite
   * nicht immer exakt zur tatsächlichen Textbreite). Wie bei `InputField`.
   */
  labelBackgroundColor?: string;
  fullWidth?: boolean;
  /**
   * Macht die Optionsliste durchsuchbar: statt des Auswahlmenüs (MUI Select, das kein
   * Eingabefeld kennt) rendert das Feld einen Autocomplete – Tippen filtert die Optionen,
   * Gruppenüberschriften bleiben erhalten. Nur bei variant='floating'; `allowCustomInput`
   * gibt es hier nicht, weil ein Suchfeld keinen Menüpunkt zum Aufklappen braucht.
   */
  searchable?: boolean;
  /**
   * Nur zusammen mit `searchable`: Meldet den getippten Suchtext nach außen, damit die
   * Aufrufseite die Optionen nachladen kann. Für Kataloge, die zu groß sind, um sie vollständig
   * in den Browser zu holen (AVV mit 842, CAS mit 3.117 Einträgen).
   *
   * Ist die Prop gesetzt, filtert das Feld die Optionen **nicht** mehr selbst – sonst würde die
   * Trefferliste des Servers ein zweites Mal gegen denselben Text gefiltert, und Treffer, die
   * über ein anderes Feld gefunden wurden (eine Nummer statt des Namens), verschwänden wieder.
   */
  onSearchInput?: (suchtext: string) => void;
  /**
   * Nur zusammen mit `searchable`: ein Eintrag am Ende der Liste, der nichts auswählt, sondern
   * eine Aktion auslöst – etwa die Liste erweitern („Anträge anderer Projekte anzeigen“, progressive
   * disclosure). Das Menü bleibt dabei offen; der Eintrag ist per Tastatur erreichbar wie jede
   * Option und erscheint auch beim Suchen.
   */
  listenAktion?: { label: string; onClick: () => void };
  /** Ermöglicht manuelle Eingabe über zusätzlichen Menüpunkt am Ende */
  allowCustomInput?: boolean;
  /** Label für den Custom-Input-Menüpunkt (Standard: "Anderen Wert eingeben...") */
  customInputLabel?: string;
  /** Typ des Eingabefelds im Custom-Modus: text oder number */
  customInputType?: "text" | "number";
  /** Platzhalter im Custom-Input-Feld */
  customInputPlaceholder?: string;
  /** Suffix für Zahlen-Eingabe (z.B. "Monate") */
  customInputSuffix?: string;
  /** Erlaubt negative Werte bei customInputType="number" */
  customInputAllowNegative?: boolean;
  /** Erlaubt Dezimalstellen bei customInputType="number" */
  customInputAllowDecimals?: boolean;
}

const getOptionValue = (option: DropdownOption): string =>
  typeof option === "string" ? option : option.value;

const getOptionLabel = (option: DropdownOption): string =>
  typeof option === "string" ? option : option.label;

const getOptionTooltip = (
  option: DropdownOption,
): React.ReactNode | undefined =>
  typeof option === "string" ? undefined : option.tooltip;

const getOptionDividerBefore = (option: DropdownOption): boolean =>
  typeof option === "string" ? false : Boolean(option.dividerBefore);

const getOptionGroupLabel = (option: DropdownOption): string | undefined =>
  typeof option === "string" ? undefined : option.groupLabel;

/**
 * NumericFormat-Wrapper für Custom-Number-Input
 */
const NumericFormatCustom = React.forwardRef<
  any,
  {
    inputRef: (instance: HTMLInputElement | null) => void;
    onChange: (event: { target: { name: string; value: string } }) => void;
    name: string;
    allowDecimals?: boolean;
    allowNegative?: boolean;
  }
>(function NumericFormatCustom(props, ref) {
  const {
    inputRef,
    onChange,
    allowDecimals = false,
    allowNegative = false,
    ...other
  } = props;
  return (
    <NumericFormat
      {...other}
      getInputRef={inputRef}
      thousandSeparator="."
      decimalSeparator=","
      allowNegative={allowNegative}
      decimalScale={allowDecimals ? undefined : 0}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.value || "",
          },
        });
      }}
    />
  );
});

/**
 *
 * variant='side-label' (Standard): Label links neben dem Select
 * variant='floating': MUI TextField select mit Floating-Label (für Dialoge)
 *
 * Mit allowCustomInput: Zusätzlicher Menüpunkt "Anderen Wert eingeben..." öffnet Eingabefeld
 */
const Dropdown: React.FC<DropdownProps> = ({
  label,
  value,
  options,
  onChange,
  getColor,
  getTextColor,
  isPlaceholder,
  disabled = false,
  width,
  labelWidth = 200,
  inputWidth,
  sx = {},
  error = false,
  helperText,
  required = false,
  tooltip,
  size,
  variant = "side-label",
  labelBackgroundColor = colors.background.paper,
  fullWidth = true,
  searchable = false,
  onSearchInput,
  listenAktion,
  allowCustomInput = false,
  customInputLabel = "Anderen Wert eingeben...",
  customInputType = "text",
  customInputPlaceholder = "Wert eingeben...",
  customInputSuffix,
  customInputAllowNegative = false,
  customInputAllowDecimals = false,
}) => {
  // Offen-Zustand des durchsuchbaren Menüs – kontrolliert, damit der Aktionseintrag
  // (`listenAktion`) es offen halten kann.
  const [suchlisteOffen, setSuchlisteOffen] = useState(false);
  const [isCustomInputMode, setIsCustomInputMode] = useState(false);
  const [customInputValue, setCustomInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isCustomInputMode && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isCustomInputMode]);

  const handleSelectChange = (event: any) => {
    const newValue = event.target.value;
    if (newValue === CUSTOM_INPUT_MARKER) {
      setIsCustomInputMode(true);
      setCustomInputValue("");
    } else {
      onChange(newValue);
    }
  };

  const handleCustomInputConfirm = () => {
    const trimmed =
      customInputType === "number"
        ? customInputValue.trim().replace(",", ".")
        : customInputValue.trim();
    if (customInputType === "number" && trimmed !== "") {
      const num = parseFloat(trimmed);
      onChange(isNaN(num) ? "" : String(num));
    } else {
      onChange(trimmed);
    }
    setIsCustomInputMode(false);
    setCustomInputValue("");
  };

  const handleCustomInputCancel = () => {
    setIsCustomInputMode(false);
    setCustomInputValue("");
  };

  /**
   * Darstellung einer Option – als Chip, wenn die Aufrufseite Farben mitgibt, sonst der
   * blanke Text. Menü (Select) und Suchliste (Autocomplete) teilen sie sich, damit dieselbe
   * Optionsliste in beiden Modi gleich aussieht.
   */
  const optionInhalt = (option: DropdownOption): React.ReactNode => {
    const optValue = getOptionValue(option);
    const optLabel = getOptionLabel(option);
    if (!getColor) return optLabel;
    return (
      <Chip
        label={optLabel}
        size="small"
        variant="filled"
        sx={{
          backgroundColor: getColor(optValue),
          color:
            getTextColor?.(optValue) ??
            (isPlaceholder?.(optValue)
              ? colors.text.disabled
              : colors.utility.black),
          fontStyle: isPlaceholder?.(optValue) ? "italic" : "normal",
          fontWeight: 500,
        }}
      />
    );
  };

  const renderMenuItems = () => {
    // Gruppenüberschrift immer dann, wenn die Gruppe gegenüber der vorherigen Option wechselt.
    // Die Reihenfolge macht damit die Aufrufseite – hier steht nur, wo die Trennung sichtbar wird.
    let letzteGruppe: string | undefined;
    const items = options.flatMap((option) => {
      const optValue = getOptionValue(option);
      const optTooltip = getOptionTooltip(option);
      const content = optionInhalt(option);
      const gruppe = getOptionGroupLabel(option);
      const gruppeBeginnt = gruppe !== undefined && gruppe !== letzteGruppe;
      letzteGruppe = gruppe;

      const menuItem = (
        <MenuItem
          key={optValue}
          value={optValue}
          sx={
            getOptionDividerBefore(option)
              ? { borderTop: `1px solid ${colors.border.divider}`, mt: 0.5 }
              : undefined
          }
        >
          {optTooltip ? (
            <Tooltip title={optTooltip} placement="right" arrow>
              <Box sx={{ display: "inline-flex" }}>{content}</Box>
            </Tooltip>
          ) : (
            content
          )}
        </MenuItem>
      );

      if (!gruppeBeginnt) return [menuItem];
      return [
        <ListSubheader
          key={`gruppe-${gruppe}`}
          sx={{
            lineHeight: 2,
            fontSize: "0.75rem",
            fontWeight: 600,
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            color: colors.text.secondary,
            // ListSubheader klebt im Menü standardmäßig oben – ohne deckenden Hintergrund
            // scrollen die Optionen sichtbar durch die Überschrift hindurch.
            backgroundColor: colors.background.paper,
          }}
        >
          {gruppe}
        </ListSubheader>,
        menuItem,
      ];
    });
    if (allowCustomInput) {
      items.push(
        <MenuItem
          key={CUSTOM_INPUT_MARKER}
          value={CUSTOM_INPUT_MARKER}
          sx={{
            fontStyle: "italic",
            borderTop: `1px solid ${colors.border.divider}`,
            mt: 0.5,
          }}
        >
          {customInputLabel}
        </MenuItem>,
      );
    }
    return items;
  };

  // ── Floating-Variante: MUI TextField select ──
  if (variant === "floating") {
    const resolvedSize = size ?? "small";
    // Maskiert den Umrandungsstrich hinter dem hochgewanderten Label (wie bei InputField).
    const floatingLabelSx = floatingLabelMaskSx(labelBackgroundColor);
    const labelNode = tooltip ? (
      <InfoTooltip title={tooltip} placement="top">
        <span>{label}</span>
      </InfoTooltip>
    ) : (
      label
    );

    if (isCustomInputMode) {
      return (
        <Box sx={{ width, ...sx }}>
          <TextField
            fullWidth={fullWidth}
            value={customInputValue}
            onChange={(e) => setCustomInputValue(e.target.value)}
            onBlur={handleCustomInputConfirm}
            onKeyDown={(e) => {
              if (e.key === "Escape") handleCustomInputCancel();
              else if (e.key === "Enter") {
                e.preventDefault();
                handleCustomInputConfirm();
              }
            }}
            label={labelNode}
            placeholder={customInputPlaceholder}
            disabled={disabled}
            error={error}
            helperText={helperText}
            required={required}
            inputRef={inputRef}
            size={resolvedSize}
            slotProps={
              customInputType === "number"
                ? {
                    input: { inputComponent: NumericFormatCustom as any },
                    htmlInput: {
                      allowDecimals: customInputAllowDecimals,
                      allowNegative: customInputAllowNegative,
                    },
                  }
                : {}
            }
            sx={{ ...floatingLabelSx, ...sx }}
          />
        </Box>
      );
    }

    if (searchable) {
      // Autocomplete arbeitet auf Objekten, nicht auf Werten – die Optionsliste wird deshalb
      // einmal übersetzt; `original` trägt die Aufrufseiten-Option für Darstellung und Gruppe.
      const suchOptionen: {
        value: string;
        label: string;
        original: DropdownOption;
      }[] = options.map((option) => ({
        value: getOptionValue(option),
        label: getOptionLabel(option),
        original: option,
      }));
      const aktionsOption = listenAktion
        ? {
            value: LISTEN_AKTION_MARKER,
            label: listenAktion.label,
            original: {
              value: LISTEN_AKTION_MARKER,
              label: listenAktion.label,
            },
          }
        : null;
      const standardFilter =
        createFilterOptions<(typeof suchOptionen)[number]>();
      const ausgewaehlt =
        suchOptionen.find((option) => option.value === value) ?? null;

      return (
        <Box sx={{ width }}>
          <MuiAutocomplete
            options={
              aktionsOption ? [...suchOptionen, aktionsOption] : suchOptionen
            }
            value={ausgewaehlt}
            // Immer kontrolliert: Der Aktionseintrag verschwindet nach dem Klick, und ein Wechsel
            // von kontrolliert zu unkontrolliert schlösse das Menü, das offen bleiben soll.
            open={suchlisteOffen}
            onOpen={() => setSuchlisteOffen(true)}
            onClose={(_, grund) => {
              // Der Aktionseintrag wählt nichts aus – das Menü bleibt dafür offen. Eine echte
              // Auswahl schließt es in onChange.
              if (grund !== "selectOption") setSuchlisteOffen(false);
            }}
            onChange={(_, neu) => {
              if (neu?.value === LISTEN_AKTION_MARKER) {
                listenAktion?.onClick();
                return;
              }
              setSuchlisteOffen(false);
              onChange(neu ? neu.value : "");
            }}
            onInputChange={
              onSearchInput
                ? (_, text, grund) => {
                    // Nur echte Tastatureingaben suchen lassen: Beim Auswählen einer Option
                    // meldet MUI deren Label als Eingabe, was sonst eine Suche nach dem eben
                    // gewählten Eintrag auslöste.
                    if (grund === "input") onSearchInput(text);
                  }
                : undefined
            }
            filterOptions={(opt, zustand) => {
              const treffer = onSearchInput
                ? opt
                : standardFilter(
                    opt.filter((o) => o.value !== LISTEN_AKTION_MARKER),
                    zustand,
                  );
              // Der Aktionseintrag steht immer am Ende, unabhängig vom Suchtext.
              return aktionsOption
                ? [
                    ...treffer.filter((o) => o.value !== LISTEN_AKTION_MARKER),
                    aktionsOption,
                  ]
                : treffer;
            }}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, gewaehlt) =>
              option.value === gewaehlt.value
            }
            // Optionen ohne Gruppe (z.B. ein führendes "– kein Wert –") liefern hier den
            // leeren String; die Überschrift dazu blendet das Paper-sx unten wieder aus.
            //
            // Immer gesetzt, auch ohne Gruppen: Wechselt `groupBy` bei offenem Menü von
            // undefined auf eine Funktion – etwa wenn `listenAktion` die Liste um eine Gruppe
            // erweitert –, rendert MUI die alte, ungruppierte Liste als gruppierte und stürzt ab
            // („Cannot read properties of undefined (reading 'map')“).
            groupBy={(option) => getOptionGroupLabel(option.original) ?? ""}
            disabled={disabled}
            fullWidth={fullWidth}
            size={resolvedSize}
            noOptionsText="Kein Treffer"
            renderOption={(props, option) => {
              const { key, ...rest } =
                props as React.HTMLAttributes<HTMLLIElement> & {
                  key: React.Key;
                };
              if (option.value === LISTEN_AKTION_MARKER) {
                return (
                  <Box
                    component="li"
                    key={key}
                    {...rest}
                    sx={{
                      borderTop: `1px solid ${colors.border.divider}`,
                      color: colors.text.secondary,
                      fontStyle: "italic",
                    }}
                  >
                    {option.label}
                  </Box>
                );
              }
              const optTooltip = getOptionTooltip(option.original);
              const inhalt = optionInhalt(option.original);
              return (
                <Box component="li" key={key} {...rest}>
                  {optTooltip ? (
                    <Tooltip title={optTooltip} placement="right" arrow>
                      <Box sx={{ display: "inline-flex" }}>{inhalt}</Box>
                    </Tooltip>
                  ) : (
                    inhalt
                  )}
                </Box>
              );
            }}
            slotProps={{
              paper: {
                sx: {
                  // Gleiche Optik wie die Gruppenüberschrift im Select-Menü.
                  "& .MuiAutocomplete-groupLabel": {
                    lineHeight: 2,
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    letterSpacing: "0.04em",
                    textTransform: "uppercase",
                    color: colors.text.secondary,
                    backgroundColor: colors.background.paper,
                  },
                  "& .MuiAutocomplete-groupLabel:empty": { display: "none" },
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={labelNode}
                error={error}
                helperText={helperText}
                required={required}
              />
            )}
            sx={{ ...floatingLabelSx, ...sx }}
          />
        </Box>
      );
    }

    return (
      <Box sx={{ width }}>
        <TextField
          select
          label={labelNode}
          value={value}
          onChange={handleSelectChange}
          fullWidth={fullWidth}
          size={resolvedSize}
          disabled={disabled}
          error={error}
          helperText={helperText}
          required={required}
          // Im Feld nur der Text der gewählten Option: Die Menüeinträge tragen bei Optionen mit
          // Tooltip eine inline-flex-Box, die MUIs Kürzung mit „…" aushebelt – der Wert liefe
          // dann unter den Pfeil. Farbige Chips behalten ihre eigene Darstellung.
          slotProps={
            getColor
              ? undefined
              : {
                  select: {
                    renderValue: (gewaehlt) => {
                      const option = options.find(
                        (kandidat) => getOptionValue(kandidat) === gewaehlt,
                      );
                      return option ? getOptionLabel(option) : "";
                    },
                  },
                }
          }
          sx={{ ...floatingLabelSx, ...sx }}
        >
          {renderMenuItems()}
        </TextField>
      </Box>
    );
  }

  // ── Side-Label-Variante (Standard) ──
  const resolvedSize = size ?? "small";

  const valueIsPlaceholder = isPlaceholder?.(value) ?? false;
  const valueTextColor =
    getTextColor?.(value) ??
    (valueIsPlaceholder ? colors.text.disabled : colors.utility.black);

  const chipStyles = getColor
    ? {
        backgroundColor: getColor(value),
        "& .MuiOutlinedInput-notchedOutline": { border: "none" },
        "& .MuiSelect-select": {
          color: valueTextColor,
          fontStyle: valueIsPlaceholder ? "italic" : "normal",
          fontWeight: 500,
          display: "flex",
          alignItems: "center",
        },
        "& .MuiSelect-icon": {
          color: valueTextColor,
          opacity: 0.7,
        },
        "&:hover .MuiSelect-icon": { opacity: 1 },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
        "&.Mui-disabled": {
          backgroundColor: colors.state.disabled,
          color: colors.state.disabledText,
        },
      }
    : {};

  // Pill-Radius, Schatten und Hover-Verstärkung kommen bereits aus dem Theme (MuiOutlinedInput).
  // Hier bleibt nur die Border-Farbe bei Hover/Fokus, die das Theme nicht pauschal setzen kann.
  const standardStyles = !getColor
    ? {
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: colors.border.focus,
        },
        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
          borderColor: colors.border.focus,
          borderWidth: 2,
        },
        "&.Mui-focused": { boxShadow: `0 4px 12px ${colors.shadow.heavy}` },
      }
    : {};

  // Höhe und Innenabstand kommen zentral aus `formField` (styles/theme.ts) – hier nur die
  // Border-Farbe bei Hover/Fokus.
  const inputFieldStyles = {
    "& .MuiOutlinedInput-root": {
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.border.focus,
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.border.focus,
        borderWidth: 2,
      },
    },
  };

  return (
    <Box sx={{ width: width, ...sx }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        {label && (
          <Typography
            sx={{
              width: labelWidth,
              minWidth: labelWidth,
              whiteSpace: "nowrap",
              color: colors.text.primary,
              fontWeight: 400,
            }}
          >
            {label}
          </Typography>
        )}

        <Box sx={{ flex: 1, width: inputWidth, maxWidth: inputWidth }}>
          {isCustomInputMode ? (
            <Box>
              <TextField
                fullWidth
                value={customInputValue}
                onChange={(e) => setCustomInputValue(e.target.value)}
                onBlur={handleCustomInputConfirm}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    handleCustomInputCancel();
                  } else if (e.key === "Enter") {
                    e.preventDefault();
                    handleCustomInputConfirm();
                  }
                }}
                placeholder={customInputPlaceholder}
                disabled={disabled}
                error={error}
                inputRef={inputRef}
                size={resolvedSize}
                slotProps={
                  customInputType === "number"
                    ? {
                        input: { inputComponent: NumericFormatCustom as any },
                        htmlInput: {
                          allowDecimals: customInputAllowDecimals,
                          allowNegative: customInputAllowNegative,
                        },
                      }
                    : {}
                }
                sx={inputFieldStyles}
              />
              {helperText && (
                <Typography
                  variant="caption"
                  color={error ? "error" : "text.secondary"}
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {helperText}
                </Typography>
              )}
            </Box>
          ) : (
            <FormControl fullWidth error={error}>
              <Select
                value={value}
                onChange={handleSelectChange}
                disabled={disabled}
                size={resolvedSize}
                sx={{
                  fontSize: "0.875rem",
                  borderRadius: `${borderRadius.field}px`,
                  boxShadow: `0 2px 4px ${colors.shadow.light}`,
                  transition: "all 0.2s ease-in-out",
                  // Höhe und Innenabstand: zentral aus `formField` (styles/theme.ts)
                  "&:hover": { boxShadow: `0 4px 8px ${colors.border.light}` },
                  ...chipStyles,
                  ...standardStyles,
                }}
              >
                {renderMenuItems()}
              </Select>
              {helperText && (
                <Typography
                  variant="caption"
                  color={error ? "error" : "text.secondary"}
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {helperText}
                </Typography>
              )}
            </FormControl>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Dropdown;
